import React, { useState, useEffect, useRef } from 'react';
import {
  DownloadIcon, FileCodeIcon, FileIcon, FileSpreadsheetIcon, FileTextIcon, ImageIcon, PaperclipIcon,
} from './icons';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

interface GeneratedFile {
  filename: string;
  size: number;
  downloadUrl: string;
  contentType?: string;
}

interface GeneratedFilesProps {
  files: GeneratedFile[];
}

const isPreviewable = (filename: string, contentType?: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ['pdf', 'docx', 'pptx', 'xlsx'].includes(ext || '');
};

const GeneratedFiles: React.FC<GeneratedFilesProps> = ({ files }) => {
  const { colors } = useTheme();
  const { getAccessToken } = useAuth();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<{[key: string]: string}>({});
  const [preview, setPreview] = useState<{ filename: string; blobUrl: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState<{url: string, filename: string} | null>(null);
  const fetchedFilesRef = useRef<Set<string>>(new Set());

  const getFileIcon = (filename: string, contentType?: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const type = contentType?.toLowerCase();

    if (type?.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext || '')) {
      return <ImageIcon size={28} strokeWidth={1.5} />;
    }
    if (['csv', 'xlsx', 'xls'].includes(ext || '') || type?.includes('csv') || type?.includes('spreadsheet')) {
      return <FileSpreadsheetIcon size={28} strokeWidth={1.5} />;
    }
    if (['json', 'html', 'py', 'js', 'ts', 'jsx', 'tsx'].includes(ext || '') || type?.includes('json') || type?.includes('html')) {
      return <FileCodeIcon size={28} strokeWidth={1.5} />;
    }
    if (['txt', 'pdf', 'docx', 'doc', 'md'].includes(ext || '') || type?.includes('text') || type?.includes('pdf')) {
      return <FileTextIcon size={28} strokeWidth={1.5} />;
    }
    return <FileIcon size={28} strokeWidth={1.5} />;
  };

  const getFileTypeLabel = (filename: string, contentType?: string) => {
    const ext = filename.split('.').pop()?.toUpperCase();
    const type = contentType?.toLowerCase();
    
    if (type?.startsWith('image/')) return 'Image';
    if (ext === 'CSV') return 'Spreadsheet';
    if (ext === 'JSON') return 'Data';
    if (ext === 'TXT') return 'Text';
    if (ext === 'HTML') return 'Web Page';
    if (ext === 'PDF') return 'Document';
    if (['PY', 'JS', 'TS'].includes(ext || '')) return 'Code';
    return ext || 'File';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const isImageFile = (filename: string, contentType?: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const type = contentType?.toLowerCase();
    return type?.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext || '');
  };

  const fetchImageAsBlob = async (file: GeneratedFile): Promise<string | null> => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const absoluteUrl = file.downloadUrl.startsWith('http') 
        ? file.downloadUrl 
        : `${API_BASE_URL}${file.downloadUrl}`;
      
      const accessToken = await getAccessToken();
      
      const response = await fetch(absoluteUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        return url;
      }
      return null;
    } catch (error) {
      console.error('Error fetching image:', error);
      return null;
    }
  };

  // Auto-download images on component mount
  useEffect(() => {
    const downloadImages = async () => {
      const imageFiles = files.filter(file => 
        isImageFile(file.filename, file.contentType) && 
        !fetchedFilesRef.current.has(file.downloadUrl) // Only fetch files we haven't fetched yet
      );
      
      if (imageFiles.length === 0) return;
      
      // Mark files as being fetched
      imageFiles.forEach(file => fetchedFilesRef.current.add(file.downloadUrl));
      
      // Fetch the images
      for (const file of imageFiles) {
        const url = await fetchImageAsBlob(file);
        if (url) {
          setImageUrls(prev => ({ ...prev, [file.filename]: url }));
        }
      }
    };

    downloadImages();
  }, [files.map(f => f.downloadUrl).join(',')]); // Only depend on file URLs

  const handleDownload = async (file: GeneratedFile) => {
    setDownloading(file.filename);
    try {
      // Convert relative URL to absolute backend URL
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const absoluteUrl = file.downloadUrl.startsWith('http') 
        ? file.downloadUrl 
        : `${API_BASE_URL}${file.downloadUrl}`;
      
      // Get the current access token from auth context
      const accessToken = await getAccessToken();
      
      // Use direct fetch approach to bypass React Router completely
      const response = await fetch(absoluteUrl, {
        method: 'GET',
        credentials: 'include', // Include cookies/auth headers
        headers: {
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Download failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setTimeout(() => setDownloading(null), 1000);
    }
  };

  const handlePreview = async (file: GeneratedFile) => {
    setPreviewLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const accessToken = await getAccessToken();

      // All types go through the preview endpoint which returns PDF
      const previewUrl = file.downloadUrl.replace('/download/', '/preview/');
      const absoluteUrl = previewUrl.startsWith('http')
        ? previewUrl
        : `${API_BASE_URL}${previewUrl}`;

      const response = await fetch(absoluteUrl, {
        method: 'GET',
        credentials: 'include',
        headers: { ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }) }
      });

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setPreview({ filename: file.filename, blobUrl });
      }
    } catch (error) {
      console.error('Preview error:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (preview) {
      window.URL.revokeObjectURL(preview.blobUrl);
    }
    setPreview(null);
  };

  if (!files || files.length === 0) return null;

  // Separate images from other files
  const imageFiles = files.filter(file => isImageFile(file.filename, file.contentType));
  const otherFiles = files.filter(file => !isImageFile(file.filename, file.contentType));

  return (
    <div style={{
      margin: '16px 0',
      padding: '16px',
      backgroundColor: colors.surfaceElevated,
      borderRadius: '12px',
      border: `1px solid ${colors.borderLight}`,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '12px',
        fontSize: '14px',
        fontWeight: '600',
        color: colors.textPrimary
      }}>
        <span style={{ marginRight: '8px', display: 'flex', color: colors.textSecondary }}><PaperclipIcon size={16} /></span>
        Generated Files ({files.length})
      </div>
      
      {/* Display images inline */}
      {imageFiles.length > 0 && (
        <div style={{ marginBottom: otherFiles.length > 0 ? '20px' : '0' }}>
          <div style={{
            fontSize: '13px',
            fontWeight: '500',
            color: colors.textMuted,
            marginBottom: '12px'
          }}>
            Generated Images ({imageFiles.length})
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            {imageFiles.map((file, index) => (
              <div
                key={`img-${index}`}
                style={{
                  backgroundColor: colors.surface,
                  borderRadius: '12px',
                  border: `1px solid ${colors.border}`,
                  overflow: 'hidden',
                  transition: 'all 0.2s ease'
                }}
              >
                {imageUrls[file.filename] ? (
                  <img
                    src={imageUrls[file.filename]}
                    alt={file.filename}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '400px',
                      objectFit: 'contain',
                      display: 'block',
                      cursor: 'pointer'
                    }}
                    onClick={() => setEnlargedImage({ url: imageUrls[file.filename], filename: file.filename })}
                  />
                ) : (
                  <div style={{
                    height: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: colors.surfaceElevated,
                    color: colors.textMuted
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      border: `2px solid ${colors.textMuted}`,
                      borderTop: `2px solid ${colors.primary}`,
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  </div>
                )}
                
                {/* Image info and download bar */}
                <div style={{
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderTop: `1px solid ${colors.borderLight}`
                }}>
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: colors.textPrimary,
                      marginBottom: '2px'
                    }}>
                      {file.filename}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: colors.textMuted
                    }}>
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(file);
                    }}
                    style={{
                      background: downloading === file.filename ? colors.surfaceElevated : colors.primary + '20',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: downloading === file.filename ? colors.textMuted : colors.primary,
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    onMouseEnter={(e) => {
                      if (downloading !== file.filename) {
                        e.currentTarget.style.backgroundColor = colors.primary;
                        e.currentTarget.style.color = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (downloading !== file.filename) {
                        e.currentTarget.style.backgroundColor = colors.primary + '20';
                        e.currentTarget.style.color = colors.primary;
                      }
                    }}
                    disabled={downloading === file.filename}
                  >
                    {downloading === file.filename ? (
                      <>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          border: `1px solid ${colors.textMuted}`,
                          borderTop: `1px solid ${colors.primary}`,
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <DownloadIcon size={14} /> Download
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Display other files as cards */}
      {otherFiles.length > 0 && (
        <div>
          {imageFiles.length > 0 && (
            <div style={{
              fontSize: '13px',
              fontWeight: '500',
              color: colors.textMuted,
              marginBottom: '12px'
            }}>
              Other Files ({otherFiles.length})
            </div>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '12px'
          }}>
            {otherFiles.map((file, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  backgroundColor: colors.surface,
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.buttonHover;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.surface;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* File Icon */}
                <div style={{
                  color: colors.primary,
                  marginRight: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '40px'
                }}>
                  {getFileIcon(file.filename, file.contentType)}
                </div>

                {/* File Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: colors.textPrimary,
                    marginBottom: '4px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {file.filename}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '12px',
                    color: colors.textMuted
                  }}>
                    <span style={{
                      backgroundColor: colors.primary + '20',
                      color: colors.primary,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '500'
                    }}>
                      {getFileTypeLabel(file.filename, file.contentType)}
                    </span>
                    <span>{formatFileSize(file.size)}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                  marginLeft: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  {/* Preview Button */}
                  {isPreviewable(file.filename, file.contentType) && (
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: colors.primary + '20',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: colors.primary,
                        transition: 'all 0.2s ease'
                      }}
                      title="Preview"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreview(file);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.primary;
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = colors.primary + '20';
                        e.currentTarget.style.color = colors.primary;
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </div>
                  )}

                  {/* Download Button */}
                  {downloading === file.filename ? (
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: `2px solid ${colors.textMuted}`,
                      borderTop: `2px solid ${colors.primary}`,
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}>
                    </div>
                  ) : (
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: colors.primary + '20',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        color: colors.primary,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      title="Download"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(file);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.primary;
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = colors.primary + '20';
                        e.currentTarget.style.color = colors.primary;
                      }}
                    >
                      <DownloadIcon size={14} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image enlargement modal */}
      {enlargedImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'pointer'
          }}
          onClick={() => setEnlargedImage(null)}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={enlargedImage.url}
              alt={enlargedImage.filename}
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />
            <div
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#333'
              }}
            >
              {enlargedImage.filename}
            </div>
            <button
              onClick={() => setEnlargedImage(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                fontSize: '18px',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {(preview || previewLoading) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={closePreview}
        >
          <div
            style={{
              position: 'relative',
              width: '90vw',
              height: '90vh',
              maxWidth: '1100px',
              background: colors.surface,
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: `1px solid ${colors.border}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: `1px solid ${colors.border}`,
              flexShrink: 0
            }}>
              <div style={{
                fontSize: '15px',
                fontWeight: '600',
                color: colors.textPrimary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                marginRight: '12px'
              }}>
                {preview?.filename || 'Loading...'}
              </div>
              <button
                onClick={closePreview}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '22px',
                  cursor: 'pointer',
                  color: colors.textSecondary,
                  padding: '4px 8px',
                  borderRadius: '6px',
                  transition: 'all 0.15s ease',
                  lineHeight: 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.surfaceElevated;
                  e.currentTarget.style.color = colors.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = colors.textSecondary;
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              {previewLoading && !preview ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  gap: '12px',
                  color: colors.textMuted,
                  fontSize: '15px'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    border: `2px solid ${colors.textMuted}`,
                    borderTop: `2px solid ${colors.primary}`,
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Loading preview...
                </div>
              ) : preview ? (
                <iframe
                  src={preview.blobUrl}
                  title={preview.filename}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratedFiles;